"use client";

import { Recycle, Sprout, Star } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function ComplianceAndStandards() {
  const intro = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const badges = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const principles = [{
    title: "Permanent Marking",
    copy: "We utilize industrial laser systems to etch identification directly into substrates. No adhesives to peel, no ink to fade. Built for the long haul.",
    caveat: "marks that outlast the asset",
    icon: Sprout,
    bg: "hsl(45 68% 47%)",
    rotate: "-6deg",
    stickerColor: "hsl(45 68% 47%)"
  }, {
    title: "Regulatory Compliance",
    copy: "We adhere to strict industry standards including MIL-SPEC and OSHA requirements. Every plate is verified against your provided specifications.",
    caveat: "compliant by design",
    icon: Star,
    bg: "hsl(214 42% 18%)",
    rotate: "4deg",
    stickerColor: "hsl(214 42% 18%)"
  }, {
    title: "Precision Engineering",
    copy: "We process your files with exact tolerances. From stainless steel tags to complex panel schedules, we deliver consistent, legible identification.",
    caveat: "specified by name",
    icon: Recycle,
    bg: "hsl(214 20% 25%)",
    rotate: "-3deg",
    stickerColor: "hsl(214 20% 25%)"
  }];
  return <section data-section-id="1113" id="compliance-and-standards" className="relative overflow-hidden py-32 md:py-44 bg-background text-foreground">
      {}
      <div aria-hidden="true" className="absolute top-0 left-0 right-0 h-1 bg-primary" />
      <div aria-hidden="true" className="absolute -top-3 left-[8%] w-24 h-8 rotate-[-4deg] border-[3px] bg-primary border-background" />
      <div aria-hidden="true" className="absolute -top-3 right-[14%] w-24 h-8 rotate-[3deg] border-[3px] bg-secondary border-background" />

      <div className="max-w-[1360px] mx-auto px-6 sm:px-10 lg:px-20">
        {}
        <div ref={intro.ref} className="max-w-[720px] mx-auto text-center transition-all duration-700" style={{
        transitionTimingFunction: "cubic-bezier(.34,1.56,.64,1)",
        transform: intro.isVisible ? "translateY(0)" : "translateY(24px)",
        opacity: intro.isVisible ? 1 : 0
      }}>
          <span className="inline-block font-mono uppercase text-xs md:text-sm font-bold tracking-[0.18em] px-3 py-1.5 border-[1.5px] rounded-md -rotate-2 text-muted-foreground border-muted-foreground">
            SECTION 05 / COMPLIANCE
          </span>

          <h2 className="font-default font-bold mt-8 text-[clamp(2.5rem,6.5vw,5.75rem)] leading-[0.95] tracking-[-0.015em] text-foreground">
            Engineered for{" "}
            <span className="relative inline-block">
              <span className="relative z-10">permanence</span>
              <svg aria-hidden="true" viewBox="0 0 320 110" className="absolute -inset-x-4 -inset-y-3 w-[calc(100%+2rem)] h-[calc(100%+1.5rem)] pointer-events-none" fill="none">
                <path d="M 30 60 Q 20 20, 90 18 Q 180 8, 260 22 Q 310 32, 300 60 Q 295 88, 220 96 Q 130 105, 60 92 Q 18 82, 30 60 Z" stroke="hsl(45 68% 47%)" strokeWidth="4" strokeLinecap="round" strokeDasharray="900" style={{
                strokeDashoffset: intro.isVisible ? 0 : 900,
                transition: "stroke-dashoffset 900ms ease-out 300ms"
              }} />
              </svg>
            </span>
            .
          </h2>

          <p className="font-default mt-8 text-lg md:text-xl leading-[1.55] text-muted-foreground">
            Industrial identification requires precision. We provide verified, compliant marking services for critical infrastructure, ensuring every tag meets your exact technical specifications.
          </p>
        </div>

        {}
        <div ref={badges.ref} className="relative mt-20 md:mt-28">
          {}
          <svg aria-hidden="true" viewBox="0 0 1200 240" preserveAspectRatio="none" className="hidden md:block absolute inset-x-0 top-16 w-full h-40 pointer-events-none" fill="none">
            <path d="M 80 120 Q 250 30, 420 130 T 780 120 Q 950 50, 1120 130" stroke="hsl(45 68% 47%)" strokeWidth="3" strokeLinecap="round" strokeDasharray="8 8" style={{
            strokeDashoffset: badges.isVisible ? 0 : 200,
            opacity: badges.isVisible ? 0.7 : 0,
            transition: "stroke-dashoffset 1200ms ease-out, opacity 600ms ease-out"
          }} />
          </svg>

          {}
          <svg aria-hidden="true" viewBox="0 0 80 800" preserveAspectRatio="none" className="md:hidden absolute left-1/2 -translate-x-1/2 top-32 bottom-32 h-[70%] w-16 pointer-events-none" fill="none">
            <path d="M 40 0 Q 10 150, 40 300 T 40 600 Q 10 720, 40 800" stroke="hsl(45 68% 47%)" strokeWidth="3" strokeLinecap="round" strokeDasharray="8 8" style={{
            opacity: badges.isVisible ? 0.6 : 0,
            transition: "opacity 600ms ease-out"
          }} />
          </svg>

          <div className="relative grid grid-cols-1 md:grid-cols-3 gap-14 md:gap-8">
            {principles.map((item, i) => {
            const Icon = item.icon;
            return <div data-index={i} key={i} className="flex flex-col items-center text-center transition-all" style={{
              transitionDuration: "700ms",
              transitionTimingFunction: "cubic-bezier(.34,1.56,.64,1)",
              transitionDelay: `${i * 120}ms`,
              transform: badges.isVisible ? "translateY(0)" : "translateY(24px)",
              opacity: badges.isVisible ? 1 : 0
            }}>
                  {}
                  <div className="relative flex items-center justify-center w-40 h-40 rounded-lg border-[4px] transition-transform duration-300 shadow-md" style={{
                backgroundColor: item.bg,
                borderColor: "hsl(215 48% 11%)",
                borderRadius: "0.5rem",
                transform: `rotate(${item.rotate})`
              }}>
                    <Icon className="w-16 h-16 text-background" strokeWidth={2.5} absoluteStrokeWidth />
                    {}
                    <span className="absolute -top-3 -right-2 font-mono text-[10px] font-bold px-2 py-1 border-[1.5px] rounded-md rotate-[8deg] bg-background text-foreground border-foreground">
                      0{i + 1}
                    </span>
                  </div>

                  <h3 className="font-default font-bold mt-10 text-[clamp(1.5rem,2.2vw,2.25rem)] leading-[1.05] tracking-[-0.01em] text-foreground">
                    {item.title}
                  </h3>

                  <p className="font-default mt-4 max-w-xs text-base md:text-[1.0625rem] leading-[1.65] text-muted-foreground">
                    {item.copy}
                  </p>

                  <p className="mt-5 text-2xl md:text-[1.75rem] leading-[1.1] font-serif text-foreground" style={{
                transform: i % 2 === 0 ? "rotate(-1.5deg)" : "rotate(2deg)"
              }}>
                    &ldquo;{item.caveat}&rdquo;
                  </p>
                </div>;
          })}
          </div>
        </div>
      </div>

      {}
      <div aria-hidden="true" className="absolute bottom-0 left-0 right-0 h-1 bg-primary" />
    </section>;
}